<template>
	<!-- Primary navigation -->
	<nav class="navigation open">
		<div 
			class="navigation__item"
			v-for="item in productConfig.mainMenu"
			:key="item.title"
			:class="{active: item.submenu === activePrimary}"
		>
			<span class="navigation__btn-caption">{{ $t(item.title) }}</span>
			<button
				class="navigation__btn"
				@click="mainMenuClickHandler(item)"
			>
				<svg class="navigation__btn-icon"><use :href="'#'+item.icon"></use></svg>
			</button>
		</div>
	</nav>

	<!-- Secondary special navigation -->
	<nav
		class="navigation navigation--secondary"
		:class="{
			'navigation--special': productConfig.specialMenu[activePrimary],
			'open': secondaryOpen
		}"
	>
		<div class="navigation__inner">
			<template
				v-for="(item, index) in productConfig.specialMenu[activePrimary]"
				:key="item.title"
			>
				<div
					class="navigation__item"
					:ref="'navitem' + index"
					:class="{'active': !hideControlPanel}"
				>
					<span
            class="navigation__btn-caption navigation__btn-caption--overlap"
            :class="$store.state.activeProduct"
          >
            {{ $t(item.title) }}
          </span>
					<button
						class="navigation__btn"
						@click="secondaryMenuClickHandler(item, index)"
					>
						<svg class="navigation__btn-icon"><use :href="'#'+item.icon"></use></svg>
						<svg class="navigation__btn-icon"><use :href="'#'+item.icon+'--hover'"></use></svg>
						<svg class="navigation__btn-icon"><use :href="'#'+item.icon+'--click'"></use></svg>
					</button>
				</div>
			</template>
		</div>
	</nav>

	<!-- Secondary navigation -->
	<nav
		class="navigation navigation--secondary"
		:class="[
    `navigation--secondary-${productConfig.secondaryMenu[activePrimary]?.length}`,
    `navigation--thumbnails-${$store.state.activeProduct}`,
    {
			'open': secondaryOpen,
			'navigation--thumbnails': ['thumbnails', 'video_thumbnails'].includes(secondaryNavStyle),
      'navigation--video-thumbnails': ['video_thumbnails'].includes(secondaryNavStyle)
		}]"
		:ref="secondaryNav"
	>
		<swiper
			ref="swiper"
			direction="vertical"
			slides-per-view="auto"
			mousewheel
			observe-parents
			observe-slide-children
			watch-slides-visibility
			watch-slides-progress
			:scrollbar="{ draggable: true }"
			@slideChange="onSwiperSlideChange"
			:class="{
				'expanded-top': secondaryExpandedTop,
				'expanded-bottom': secondaryExpandedBottom
			}"
		>
			<template
				v-for="(item, index) in productConfig.secondaryMenu[activePrimary]"
				:key="item.title"
			>
				<!-- First level – thumbnails -->
				<swiper-slide
					v-if="['thumbnails', 'video_thumbnails'].includes(item.style)"
					class="navigation__item navigation__item--thumbnail"
					:class="{ 'active': item.title === activeSecondary }"
				>
					<span class="navigation__btn-caption" :class="{ autohide: item.autoHideLabel }" v-html="$t(item.title)" />
					<button
						class="navigation__thumb-btn navigation__thumb-btn--video"
						:class="{ 
              'active': item.title === activeSecondary,
              'navigation__thumb-btn--wide': item.wide 
            }"
						:style="{ backgroundImage: getThumbnailPath(item.image) }"
						@click="secondaryMenuClickHandler(item, index)"
					>
						<svg v-if="item.style === 'video_thumbnails'"><use href="#icon-play"></use></svg>
					</button>
				</swiper-slide>
				<!-- First level – simple -->
				<swiper-slide
					v-else-if="!('visible' in item) || ('visible' in item && item.visible)"
					:ref="'navitem' + index"
          @mouseover="secondaryMenuHover(true)"
          @mouseleave="secondaryMenuHover(false)"
					class="navigation__item"
					:class="{
            'active': !item.toggle && (item.invertActiveStatus ? item.title !== activeSecondary : item.title === activeSecondary),
						'navigation__item--with-options': 'options' in item,
						'navigation__item--with-thumbs': 'options' in item && item.options.style === 'thumbnails'
					}"
				>
					<span class="navigation__btn-caption" :class="{ autohide: item.autoHideLabel }" v-html="$t(item.title)" />
					<button
						class="navigation__btn"
						:class="item.animation"
						@click="secondaryMenuClickHandler(item, index)"
					>
						<svg class="navigation__btn-icon"><use :href="'#'+item.icon"></use></svg>
						<svg class="navigation__btn-icon"><use :href="'#'+item.icon+'--hover'"></use></svg>
						<svg class="navigation__btn-icon"><use :href="'#'+item.icon+'--click'"></use></svg>
					</button>
					<div
						v-if="'options' in item"
						class="navigation__options"
						:class="'navigation__options--' + item.options.style"
					>
						<!-- Second level – list -->
						<template v-if="item.options.style === 'vertical'">
							<button
								v-for="option in item.options.items"
								:key="option.title"
								class="navigation__option"
								:class="{ 'active': option.title === activeOptions[item.title] }"
								@click="optionsMenuClickHandler(option, item.title)"
							>
								<svg v-if="option.icon" class="navigation__btn-icon"><use :href="'#' + option.icon"></use></svg>
								<img v-if="option.image" class="navigation__btn-image" :src="getThumbnailPath(option.image, false)" />
								{{ $t(option.title) }}
							</button>
						</template>
						<!-- Second level – thumbnails -->
						<template v-else-if="item.options.style === 'thumbnails'">
							<button
								v-for="option in item.options.items"
								:key="option.title"
								class="navigation__thumb-btn"
								:class="{ 'active': option.title === activeOptions[item.title] }"
								:style="{ backgroundImage: getThumbnailPath(option.image) }"
								@click="optionsMenuClickHandler(option, item.title)"
							>
								<div class="navigation__btn-checkmark">
									<svg><use href="#icon-checkmark"></use></svg>
								</div>
								<span class="navigation__btn-caption navigation__btn-caption--checkmark">{{ $t(option.title) }}</span>
							</button>
						</template>
					</div>
				</swiper-slide>
			</template>
		</swiper>
	</nav>

</template>

<script>
import { mapState } from 'vuex'

import SwiperCore, { Scrollbar, Mousewheel } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'

SwiperCore.use([Scrollbar, Mousewheel])

export default {
	name: 'Navigation',
	components: {
		Swiper,
		SwiperSlide,
	},
	data() {
		return {
			activeOptions: {},
			secondaryExpandedTop: false,
			secondaryExpandedBottom: false
		}
	},
	computed: {
		...mapState({
			secondaryOpen: state => state.navigation.secondaryOpen,
			activePrimary: state => state.navigation.activePrimary,
			activeSecondary: state => state.navigation.activeSecondary,
      activeControls: state => state.navigation.activeControls,
			activeOptions: state => state.navigation.activeOptions,
      transferModeOn: state => state.navigation.transferModeOn,
      siderailsCheck: state => state.navigation.siderailsCheck,
			hideControlPanel: state => state.hideControlPanel,
			productConfig: state => state.productConfig
		}),
		secondaryNavStyle() {
			return this.productConfig.secondaryMenu[this.activePrimary]?.[0]?.style
		}
	},
	mounted() {
		this.$nextTick(() => {
			this.onSwiperSlideChange(this.$refs.swiper.$el.swiper)
		})

		this.$bus.on('selectModification', (modificationName, optionName) => {
			const modificationsMenu = this.productConfig.secondaryMenu?.modifications
			if (!modificationsMenu) return

			const item = modificationsMenu.find(it => it.title === modificationName)
			if (!item?.options?.items.length) return

			const option = item.options.items.find(opt => opt.title === optionName)
			!!option && this.optionsMenuClickHandler(option, modificationName)
		})
	},
	watch: {
		activePrimary(val) {
			for (const ref in this.$refs) {
				const refEl = this.$refs[ref]?.$el
				if (refEl) {
					refEl.style.width = ''
				}
			}

			this.$nextTick(() => {
				this.$refs.swiper.$el.swiper.scrollbar.updateSize()
				// automatically select first secondary menu item if it's the only one
				const secondaryMenuItems = this.productConfig.secondaryMenu[val]
				if (this.productConfig.secondaryMenu[val]?.length === 1) {
					setTimeout(() => {
						this.secondaryMenuClickHandler(secondaryMenuItems[0], 0)
					}, 150)
				}
			})
		}
	},
	methods: {
		mainMenuClickHandler(item) {
      if (item.title === 'key_features') this.$bus.emit('requestModelAnimationsReset')
			this.$store.dispatch('navigation/setMainMenuItem', item)
		},

		secondaryMenuClickHandler(item, itemIndex) {
			const secondaryMenuItems = document.querySelectorAll('.navigation--secondary .navigation__item')
			secondaryMenuItems.forEach(el => el.style = '')

			if ('modelParts' in item) {
				item.modelParts.forEach(it => {
					if ('visible' in it) this.$bus.emit('requestModelPartsVisibility', [it])
					if ('material' in it) this.$bus.emit('requestModelPartsMaterial', [it])
					if ('texture' in it) this.$bus.emit('requestModelPartsTexture', [it])
				})
			}

      // set siderails checking 
      if (item.animation === 'folding_of_foot_siderails') {
        if (this.activeControls.includes(item.animation)) {
          this.$store.commit('navigation/setSiderailsCheck', false)
        } else {
          this.$store.commit('navigation/setSiderailsCheck', true)
        }
      }

			if (this.activeSecondary === item.title) {
				// same button has been clicked twice
				this.$store.commit('navigation/setActiveSecondary', '')
				this.$store.commit('setExplanationPaneOpen', false)

        if (!('autoHideLabel' in item)) {
          this.$store.commit('setHideControlPanel', false)
        }

				if ('animation' in item) {
          if ('autoHideLabel' in item) {
            this.$store.commit('navigation/toggleActiveControl', item.animation)
            this.requestModelAnimation({ animation: item.animation, direction: this.activeControls.includes(item.animation) ? 'up' : 'down' })
          } else {
						this.requestModelAnimation({ animation: item.animation, direction: 'down' })
          }
				}
				this.$nextTick(() => {
					this.onSwiperSlideChange(this.$refs.swiper.$el.swiper)
				})
				return
			}

			if ('animation' in item) {
        if ('autoHideLabel' in item) {
          this.$store.commit('navigation/toggleActiveControl', item.animation)
          this.requestModelAnimation({ animation: item.animation, direction: this.activeControls.includes(item.animation) ? 'up' : 'down' })
        } else {
          this.requestModelAnimation({ animation: item.animation })
        }
			}

			if ('options' in item) {
				// find the widest item to set the correct width for options menu
				const menuItemEl = this.$refs['navitem' + itemIndex].$el
				const optionsMenuEls = [
					...menuItemEl.getElementsByClassName('navigation__option'),
					...menuItemEl.getElementsByClassName('navigation__btn-caption')
				]
				optionsMenuEls.forEach(it => { it.style = '' }) // reset previously set style for option items

				const widestEl = Math.max( ...optionsMenuEls.map(it => it.clientWidth) )
				const finalWidth = (widestEl + 30) + 'px'

				menuItemEl.style.width = finalWidth
				optionsMenuEls.forEach(it => { it.style.width = finalWidth })

				if (!this.activeOptions[item.title] && 'default' in item.options) {
					this.activeOptions[item.title] = item.options.items[item.options.default].title
				}
			}

			if ('explanation' in item) {
				this.$store.commit('setExplanationPaneContent', this.productConfig.explanations[item.explanation])
				this.$store.commit('setExplanationPaneOpen', true)
			}

			if ('camera' in item) {
				this.$bus.emit('requestCameraPosition', item.camera)
			}

			if ('controlPanel' in item) {
				this.$store.commit('setHideControlPanel', !this.hideControlPanel)
			}

			this.$store.commit('navigation/setActiveSecondary', item.title)

			this.$nextTick(() => {
				this.onSwiperSlideChange(this.$refs.swiper.$el.swiper)
			})
		},

		secondaryMenuHover(isHovered) {
			if (window.innerHeight <= 890) {
				this.$store.commit('navigation/setSecondaryHover', isHovered)
			}
		},

		optionsMenuClickHandler(option, itemTitle) {
			this.activeOptions[itemTitle] = option.title
			if ('animation' in option) {
				this.requestModelAnimation(option)
			}
			if ('modelParts' in option) {
				option.modelParts.forEach(it => {
					if ('visible' in it) this.$bus.emit('requestModelPartsVisibility', [it])
					if ('material' in it) this.$bus.emit('requestModelPartsMaterial', [it])
					if ('texture' in it) this.$bus.emit('requestModelPartsTexture', [it])
				})

				if (option.title === 'modifications.mattress.duocoat' || option.title === 'modifications.mattress.regular') {
					this.$bus.emit('requestMattressChange', option.title)
				}
			}
			if ('color' in option) {
				this.$bus.emit('requestColorChange', option.color)
			}
		},

		requestModelAnimation(params) {
			this.$bus.emit('requestModelAnimation', {
				name: params.animation, 
				direction: params.direction
			})
		},

		getThumbnailPath(imageName, withUrl = true) {
			if (!imageName) return null
			const image = require(`@/assets/images/products/${this.$store.state.activeProduct}/navigation/` + imageName)
			return withUrl ? 'url(' + image + ')' : image
		},

		onSwiperSlideChange(swiper) {
			if (swiper.visibleSlides) {
				const slides = [...swiper.slides]
				const selectedItemIndex = slides.findIndex(it => it.classList.contains('active'))
				const visibleSlides = [...swiper.visibleSlides]
				if (swiper.progress < 1) {
					swiper.visibleSlides[visibleSlides.length - 1].classList.remove('swiper-slide-visible')
					if (selectedItemIndex < slides.length - 1) {
						visibleSlides.pop()
					}
				}

				this.secondaryExpandedTop = slides[selectedItemIndex] == visibleSlides[0]
				this.secondaryExpandedBottom = slides[selectedItemIndex] == visibleSlides[visibleSlides.length - 1]
			}
		}
	}
}
</script>

<style lang="scss" scoped>
$navigationWidth: 60px;
$navigationItemHeight: $navigationWidth * 0.75;

.navigation {
	display: flex;
	flex-direction: column;
	position: absolute;
	left: 0;
	top: 110px;
	pointer-events: none;
	transition: transform 400ms ease 600ms;

	.loading & {
		transform: translateX(-100%);
		transition: transform 400ms ease 0ms;
	}

	&__btn {
		pointer-events: all;
		&.siderails {
			background-image: url('/images/ave2/siderails@2x.png');
			background-position: center;
			background-repeat: no-repeat;
			background-size: 95%;
		}
		&.rectable_steel_tray {
			background-image: url('/images/ave2/retractable_steel_tray@2x.png');
			background-position: center;
			background-repeat: no-repeat;
			background-size: 95%;
		}
	}

	&__item {
		position: relative;
		pointer-events: all;

		&:not(&--thumbnail) {
			display: flex;
			width: $navigationWidth;
			max-height: $navigationItemHeight;
			border-radius: 0 14px 14px 0;
			transition: width 300ms ease 300ms, max-height 400ms ease;

			&::after {
				content: "";
				position: absolute;
				left: 0;
				top: 0;
				width: 90%;
				height: 1px;
				background-color: $clrGrey200;
				transition: width 200ms ease;
			}

			svg {
				width: 100%;
				height: 100%;
				fill: $clrGrey200;
				pointer-events: none;

				&:nth-child(2), &:nth-child(3) {
					display: none;
				}
			}
		}

		span {
			display: inline-block;
			position: absolute;
			padding-left: 75px;
			top: math.div($navigationItemHeight, 2);
			left: 0;
			opacity: 0;
			text-align: left;
			font-size: 14px;
			color: $clrWhite;
			white-space: nowrap;
			transform: translate(-100px, -50%);
			transition: transform 200ms ease, opacity 200ms ease, z-index 600ms;
			pointer-events: none;
		}
		@for $i from 1 through 10 {
			&:nth-child(#{$i}) span {
				transition-delay: $i * 50 + ms;
			}
		}

		&--thumbnail {
			background-color: transparent !important;
			> span {
				top: 50%;
				padding-left: 135px;
				opacity: 1;
				transform: translate(0, -50%);
			}
			&.active > span {
				transform: translate(20px, -50%) !important;
			}
		}
		&--thumbnail + &--thumbnail {
			margin-top: -2px;
		}

		&--with-options {
			flex-direction: column;

			&.active {
				width: 200px;
				max-height: 500px;
				transition: width 300ms ease, max-height 400ms ease 300ms;
				> .navigation__btn {
					border-bottom-right-radius: 0 !important;
				}
			}
		}

		&:first-child {
			> .navigation__btn {
				border-radius: 0 14px 0 0;
				padding-bottom: 2px;
			}
			&::after {
				width: 0;
				height: 0;
			}
		}
		&:last-child {
			.navigation__btn {
				border-radius: 0 0 14px 0;
			}
		}
		&:only-child {
			.navigation__btn {
				border-radius: 0 14px 14px 0;
			}
		}

		> .navigation__btn {
			background-color: $clrGrey300;
			position: relative;
			width: $navigationWidth;
			height: $navigationItemHeight;
			padding: 3px;
		}

		&:hover, &.active {
			&::after {
				width: $navigationWidth;
			}
			> button {
				background-color: $clrGrey400;
				svg {
					fill: $clrWhite;
				}
			}
			+ * {
				&::after {
					width: $navigationWidth;
				}
			}
			.navigation__btn-caption {
				font-weight: 600;

        &--overlap:not(.sprint) {
          @media screen and (max-height: 720px) {
            opacity: 0!important;
          }
        }
        &--overlap.sprint {
          @media screen and (max-height: 775px) {
            opacity: 0!important;
          }
        }
			}
		}
		.open &.active {
			.navigation__option {
				width: 100%;
			}

			span {
				transition: transform 200ms ease, opacity 200ms ease, z-index 0ms;
				&:not(.autohide) {
					opacity: 1;
					transform: translate(0, -50%);
				}
			}
		}
	}

	&:hover {
		.navigation__item {
			span {
				opacity: 1;
				transform: translate(0, -50%);
			}
		}
	}

  &__btn-caption {
    &--checkmark {
      font-weight: 400!important;

      .navigation__thumb-btn.active & {
        font-weight: 600!important;
      }
    }
  }

	&__thumb-btn {
		position: relative;
		display: block;
		width: 120px;
		height: 120px;
		max-height: initial;
		opacity: 0;
		border-radius: 0;
		background-color: $clrWhite;
		background-size: 140px 120px;
		background-position: left center;
		background-repeat: no-repeat;
		border: 2px solid $clrWhite;
		border-left: none;
		transform: translate3d(-80px, 0, 0.1px);
		transition: all 200ms ease;
		pointer-events: all;

		.open & {
			transform: translate3d(0, 0, 0.1px);
		}
		.open .navigation__item.active &,
		.open .navigation__item > & {
			opacity: 1;
		}
		.open .swiper-slide.active &.active {
			border-top: 2px solid $clrWhite;
			border-bottom: 2px solid $clrWhite;
			border-radius: 0 15px 15px 0;
			width: 140px;
		}

		&::after {
			content: "";
			position: absolute;
			top: 0;
			left: 0;
			width: 100% !important;
			height: 100% !important;
			background-color: transparent;
			backdrop-filter: brightness(1);
			transition: all 200ms ease !important;
		}
		&:first-child::after {
			border-radius: 0 14px 0 0;
		}
		&:last-child::after {
			border-radius: 0 0 14px 0;
		}

    &:hover::after {
      background-color: rgba($color: #000000, $alpha: 0.3);
    }
    &.active::after {
      border-radius: 0 14px 14px 0;
      background-color: rgba($color: #000000, $alpha: 0.3);
    }
    &--video:hover::after {
      background-color: rgba($color: #000000, $alpha: 0.3);
    }
    &--video.active::after {
      background-color: rgba($color: #000000, $alpha: 0.6);
    }

    &--wide {
      background-size: auto 120px;
			background-position: -28px center;
    }

		.navigation__item:last-child > & {
			border-bottom-width: 4px !important;
		}

		> span {
			top: 50%;
			padding-left: 135px;
		}
		&.active > span {
			transform: translate(20px, -50%) !important;
		}

		> svg {
			width: 32px;
			height: 32px;
			fill: #ffffff;
			position: absolute;
			top: 50%;
			left: calc(50%);
			transform: translate(-50%, -50%);
			z-index: 1;
			opacity: 0;
			transition: opacity 200ms ease;
		}
		&:hover > svg,
		&.active > svg {
			opacity: 1;
		}
	}
		&__options &__thumb-btn {
			&:first-child {
				border-top-right-radius: 14px;
			}
			&:last-child {
				border-bottom-right-radius: 14px;
			}
		}

		&__thumb-btn + &__thumb-btn {
			margin-top: -2px;
		}

		&__thumb-btn &__btn-checkmark {
			display: inline-flex;
			position: absolute;
      z-index: 1;
			right: 10px;
			bottom: 10px;
			width: 18px;
			height: 18px;
			color: $clrWhite;
			background-color: $clrGrey250;
			border: 2px solid $clrGrey100;
			border-radius: 20px;
			transition: all 200ms ease;

			svg {
				opacity: 0;
				transform: scale(0.8);
				transition: all 200ms ease;
			}
		}
			&__thumb-btn.active &__btn-checkmark {
				background-color: $clrGreen;

				svg {
					opacity: 1;
					transform: scale(1);
				}
			}

	&__options {
		overflow: hidden;

		&--vertical {
			background-color: white;
			border-radius: 0 14px 14px 0;

			> button {
				color: $clrGrey400;

				img {
					top: 50%;
					left: math.div($navigationWidth, 2);
					width: auto;
					height: 100%;
					position: absolute;
					transform: translate(-50%, -50%);
				}
			}
		}
	}

	&__option {
		position: relative;
		height: $navigationWidth * 0.75;
		text-align: left;
		font-size: 14px;
		color: $clrWhite;
		white-space: nowrap;
		padding: 5px 5px 5px 75px;
		border-bottom: 1px solid $clrGrey200;

		&:last-child {
			border: none;
			border-radius: 0 0 14px 0;
		}

		svg {
			position: absolute;
			width: 60px !important;
			height: 42px !important;
			left: 0;
			top: 0;
		}

		&:hover, &.active {
			font-weight: 600;
			background-color: $clrGrey150;
		}
	}

	&--secondary {
		top: 330px;
		max-height: calc(100vh - 330px);
		transform: translateX(-100%);
		transition: transform 400ms ease;

		.navigation__item {
			> .navigation__btn {
				background-color: $clrGrey100;
			}

			&:hover:not(.active) {
				> .navigation__btn {
					background-color: $clrGrey150;
					svg {
						&:nth-last-child(3) {
							display: none;
						}
						&:nth-last-child(2) {
							display: inline;
						}
					}
				}
			}

			> .navigation__btn:active,
			&.active > .navigation__btn {
				background-color: $clrRed !important;
				svg {
					&:nth-last-child(3) {
						display: none !important;
					}
					&:nth-last-child(2) {
						display: none !important;
					}
					&:last-child {
						display: inline !important;
					}
				}
			}

			&--with-thumbs {
				background-color: transparent;

				> .navigation__btn {
					background-color: white;
				}

				&.active {
					transition: all 400ms ease;
				}
			}
		}

		&.open {
			transform: translateX(0);
		}
	}

	&--special + &--secondary {
		top: 410px;
	}

	&--thumbnails {
		height: 238px;

		&::before {
			content: "";
			width: 120px;
			height: calc(100% - 2px);
			position: absolute;
			background-color: #fff;
			border-radius: 0 15px 15px 0;
		}

		::v-deep(.swiper-container) {
			margin: 0;
			// @media screen and (min-height: 720px) {
				clip-path: path("M 0 2 L 104 2 C 111.732 2 118 8.268 118 16 L 498 16 L 498 337.993 L 118 337.993 C 118 347.725 111.7 351.993 104 351.993 L 0 351.993 L 0 2 Z");
			// }
			@media screen and (min-height: 840px) {
				clip-path: path("M 0 2 L 104 2 C 111.732 2 118 8.268 118 16 L 498 16 L 498 456 L 118 456 C 118 465.732 111.7 470 104 470 L 0 470 L 0 2 Z");
			}
			@media screen and (min-height: 960px) {
				clip-path: path("M 0 2 L 104 2 C 111.732 2 118 8.268 118 16 L 498 16 L 498 573.99 L 118 573.99 C 118 583.722 111.7 587.99 104 587.99 L 0 587.99 L 0 2 Z");
			}
			transition: clip-path 200ms ease;

			&.expanded-top {
				// @media screen and (min-height: 720px) {
					clip-path: path("M 0 0 L 126 0 C 133.7 0 140 6.3 140 14 L 498 14 L 498 338.043 L 118 338.043 C 118 347.743 111.7 352.043 104 352.043 L 0 352.043 L 0 0 Z");
				// }
				@media screen and (min-height: 840px) {
					clip-path: path("M 0 0 L 126 0 C 133.7 0 140 6.3 140 14 L 498 14 L 498 456 L 118 456 C 118 465.7 111.7 470 104 470 L 0 470 L 0 0 Z");
				}
				@media screen and (min-height: 960px) {
					clip-path: path("M 0 0 L 126 0 C 133.7 0 140 6.3 140 14 L 498 14 L 498 574.002 L 118 574.002 C 118 583.702 111.7 588.002 104 588.002 L 0 588.002 L 0 0 Z");
				}
			}
			&.expanded-bottom {
				// @media screen and (min-height: 720px) {
					clip-path: path("M 0 2 L 104 2 C 111.7 2 118 8.3 118 16 L 498 16 L 498 340.04 L 140 340.04 C 140 349.74 133.7 354.04 126 354.04 L 0 354.04 L 0 2 Z");
				// }
				@media screen and (min-height: 840px) {
					clip-path: path("M 0 2 L 104 2 C 111.7 2 118 8.3 118 16 L 498 16 L 498 458 L 140 458 C 140 467.7 133.7 472 126 472 L 0 472 L 0 2 Z");
				}
				@media screen and (min-height: 960px) {
					clip-path: path("M 0 2 L 104 2 C 111.7 2 118 8.3 118 16 L 498 16 L 498 576 L 140 576 C 140 585.7 133.7 590 126 590 L 0 590 L 0 2 Z");
				}
			}

			.swiper-slide {
				height: auto;
				pointer-events: none;
				&:last-child {
					.navigation__thumb-btn, .navigation__thumb-btn::after {
						border-bottom-right-radius: 15px;
					}
				}
			}

			.swiper-scrollbar {
				left: 3px;
				right: auto;
				width: 7px;

				&::before {
					content: "";
					position: absolute;
					top: -1%;
					left: -3px;
					width: 13px;
					height: 102%;
					background-color: #FFFFFF;
				}
			}

			.swiper-scrollbar-drag {
				background-color: $clrGrey250;
			}
		}


		&.navigation--secondary-3 {
			@media screen and (min-height: 720px) {
				height: 356px;
			}
		}
		&.navigation--secondary-4 {
			@media screen and (min-height: 720px) {
				height: 356px;
			}
			@media screen and (min-height: 840px) {
				height: 476px;
			}
		}
		&.navigation--secondary-5,
		&.navigation--secondary-6,
		&.navigation--secondary-7,
		&.navigation--secondary-8 {
			@media screen and (min-height: 720px) {
				height: 356px;
			}
			@media screen and (min-height: 840px) {
				height: 476px;
			}
			@media screen and (min-height: 960px) {
				height: 592px;
			}
		}
	}

	::v-deep(.swiper-container) {
		width: 600px;
	}

}
</style>
